'use client'

import { cn } from '@/lib/utils'
import { useState } from 'react'

type OtpCodeInputProps = {
  id: string
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
  autoFocus?: boolean
}

/**
 * One real input drawn as digit boxes. Keeping a single native field preserves paste,
 * `one-time-code` autofill and the `<label htmlFor>` link; the boxes are decoration only.
 */
export function OtpCodeInput({ id, value, onChange, length = 6, disabled, autoFocus }: OtpCodeInputProps) {
  const [focused, setFocused] = useState(false)
  const activeIndex = Math.min(value.length, length - 1)

  return (
    <div className="relative">
      <input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, length))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern={`\\d{${length}}`}
        // No maxLength: it would cut a pasted "Code: 123456" to "Code: " before digits are extracted.
        required
        disabled={disabled}
        autoFocus={autoFocus}
        className="absolute inset-0 z-10 size-full cursor-text bg-transparent text-transparent caret-transparent outline-none selection:bg-transparent disabled:cursor-not-allowed"
      />
      <div aria-hidden className="grid gap-2" style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}>
        {Array.from({ length }, (_, index) => {
          const digit = value[index]
          const isActive = focused && index === activeIndex
          return (
            <div
              key={index}
              className={cn(
                'flex aspect-square items-center justify-center rounded-xl border bg-zinc-900/60 text-2xl font-semibold tabular-nums text-zinc-50 transition-colors',
                isActive ? 'border-brand-accent ring-2 ring-brand-accent/30' : digit ? 'border-zinc-500' : 'border-zinc-700/80',
                disabled && 'opacity-60',
              )}
            >
              {digit ?? (isActive ? <span className="h-6 w-px animate-pulse bg-brand-accent" /> : null)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
